import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FileSignature, PlusCircle, Download, Trash2, Loader2, ArrowUpRight } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { directDraftsApi } from '../services/api';
import { useDraftsStore } from '../store/draftsStore';
import { Draft, DRAFT_TYPE_LABELS } from '../types';
import { EmptyState, SkeletonCard, Badge } from '../components/ui';

export function DraftsPage() {
  const navigate = useNavigate();
  const { drafts, lastFetched, fetchDrafts, fetchTypes, types, removeDraft } = useDraftsStore();
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  // Not filed = drafts without a case (IA, July 2026) — unfiled work stays findable.
  const [view, setView] = useState<'all' | 'unfiled'>('all');

  useEffect(() => {
    fetchDrafts();
    fetchTypes();
  }, [fetchDrafts, fetchTypes]);

  const labelFor = (type: string) =>
    types.find((t) => t.type === type)?.label ?? DRAFT_TYPE_LABELS[type] ?? type;

  const handleDownload = async (draft: Draft) => {
    setDownloadingId(draft.id);
    try {
      const response = await directDraftsApi.downloadDocx(draft.id);
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${draft.title.replace(/\s+/g, '-').toLowerCase()}.docx`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDelete = async (draft: Draft) => {
    if (!confirm('Delete this draft?')) return;
    await directDraftsApi.remove(draft.id);
    removeDraft(draft.id);
  };

  const isFirstLoad = lastFetched === null;
  const shownDrafts = view === 'unfiled' ? drafts.filter((d) => !d.matter_id) : drafts;

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl font-medium text-legal-900">Drafts</h1>
          <p className="text-legal-500 mt-1.5 text-[15px]">
            Notices, petitions, applications — first drafts in a minute, no research required.
          </p>
        </div>
        <Link
          to="/app/drafts/new"
          className="flex items-center space-x-2 px-4 py-2.5 bg-accent text-legal-900 rounded-lg text-sm font-semibold hover:bg-accent-400 transition-colors press shrink-0"
        >
          <PlusCircle className="h-4 w-4" />
          <span>New draft</span>
        </Link>
      </div>

      {/* All | Not filed (drafts without a case) */}
      <div className="flex items-center gap-1.5 mb-4">
        {(
          [
            { value: 'all', label: 'All' },
            { value: 'unfiled', label: 'Not filed' },
          ] as const
        ).map((f) => (
          <button
            key={f.value}
            type="button"
            onClick={() => setView(f.value)}
            data-track={`drafts-filter-${f.value}`}
            className={`px-3.5 py-2 rounded-full text-sm font-medium transition-colors press ${
              view === f.value
                ? 'bg-accent text-legal-900'
                : 'bg-white border border-legal-200 text-legal-600 hover:border-legal-300'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {isFirstLoad ? (
        <SkeletonCard />
      ) : shownDrafts.length === 0 ? (
        view === 'unfiled' && drafts.length > 0 ? (
          <EmptyState
            icon={FileSignature}
            title="Everything is filed"
            message="Every draft here is already filed under a case."
          />
        ) : (
        <EmptyState
          icon={FileSignature}
          title="No drafts yet"
          message="Pick a document type — a S.138 demand notice, a bail application, an RTI — fill in the facts, and get a court-style first draft."
          action={
            <button
              onClick={() => navigate('/app/drafts/new')}
              className="px-5 py-2.5 bg-accent text-legal-900 rounded-lg text-sm font-semibold hover:bg-accent-400 transition-colors press"
            >
              Draft your first document
            </button>
          }
        />
        )
      ) : (
        <div className="bg-white rounded-card border border-legal-200 divide-y divide-legal-100">
          {shownDrafts.map((draft) => (
            <div key={draft.id} className="flex items-center justify-between p-4">
              {/* Every row opens the canonical draft viewer (spec §4.4) —
                  the old memo-drafts-open-the-job-page split is gone. */}
              <button
                onClick={() => navigate(`/app/drafts/${draft.id}`)}
                data-track="drafts-row-open"
                className="text-left flex-1 min-w-0 mr-3"
              >
                <p className="text-sm font-medium text-legal-900 truncate hover:text-accent-700 transition-colors">
                  {draft.title}
                </p>
                <p className="text-xs text-legal-400 mt-0.5 flex items-center gap-1.5 flex-wrap">
                  <span>{labelFor(draft.draft_type)}</span>
                  <span>·</span>
                  <span>{formatDistanceToNow(new Date(draft.created_at), { addSuffix: true })}</span>
                  {draft.job_id ? (
                    <Badge tone="info" className="ml-1">Cited from your memo</Badge>
                  ) : (
                    <Badge tone="neutral" className="ml-1">Standard draft</Badge>
                  )}
                </p>
              </button>
              <div className="flex items-center space-x-1">
                <button
                  onClick={() => handleDownload(draft)}
                  disabled={downloadingId === draft.id}
                  className="p-2 text-legal-400 hover:text-legal-700 hover:bg-legal-50 rounded-md transition-colors disabled:opacity-60"
                  title="Download as Word"
                >
                  {downloadingId === draft.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4" />
                  )}
                </button>
                <button
                  onClick={() => handleDelete(draft)}
                  className="p-2 text-legal-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors"
                  title="Delete draft"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Contextual tools placement (spec §4.4) — serving a notice usually
          starts with a limitation question. */}
      <p className="mt-6 text-center">
        <a
          href="/tools"
          target="_blank"
          rel="noopener noreferrer"
          data-track="drafts-tools-link"
          className="inline-flex items-center gap-1 text-sm text-legal-500 hover:text-accent-700 transition-colors"
        >
          Check limitation periods <ArrowUpRight className="h-3.5 w-3.5" />
        </a>
      </p>
    </div>
  );
}
